<template>
  <div>
    <v-navigation-drawer
      ref="customiseDrawer"
      v-model="valueLocal"
      app
      right
      temporary
      class="v-navigation-drawer--width-max"
      stateless
    >
      <div class="drawer-container d-flex flex-column justify-space-between">
        <div
          class="drawer-header d-flex align-center justify-space-between pa-5 pl-6"
          :class="
            isOpenDrawerRentProtectionLegalExpensesEligibilityCheck
              ? 'hide-content'
              : ''
          "
        >
          <h3 class="font-28 font-weight-bold">Customise your cover</h3>
        </div>

        <div class="drawer-main flex-grow-1 pa-1 pa-xl-3">
          <BaseAlert
            v-if="coverType"
            color="var(--blue-100)"
            class="ma-2 pa-4 border-sm border-solid border-info"
          >
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center">
                <v-icon color="var(--blue-500)" class="mr-3"
                  >mdi-information</v-icon
                >
                <p class="ma-0">
                  We are showing you policies with<br />
                  <span class="font-weight-bold">{{ coverType }}</span> cover.
                </p>
              </div>
              <div>
                <BaseButton
                  text="Edit cover"
                  flat
                  link
                  icon="mdi-pencil"
                  depressed
                  color="var(--blue-500)"
                  ripple
                  small
                  class="font-weight-medium px-1 d-flex align-self-center"
                  @click="editCover"
                />
              </div>
            </div>
          </BaseAlert>

          <div class="px-3" :class="coverType ? 'mt-10' : 'mt-2'">
            <template v-if="isBuildingsCover">
              <div class="d-flex align-center">
                <h4 class="font-24 font-lg-20">Buildings cover</h4>
                <div class="ml-2">
                  <BaseTooltip
                    :text="TOOL_TIP_TEXT.Buildings"
                    icon="mdi-help-circle"
                  />
                </div>
              </div>
              <div class="d-flex flex-column flex-lg-row mt-2 align-xl-center">
                <div style="align-self: baseline">
                  <v-icon size="23" class="mr-1" color="#178748"
                    >mdi-check-circle</v-icon
                  >
                </div>
                <div class="flex-wrap mr-2 flex-basis-85">
                  This policy provides
                  <span class="font-weight-bold"
                    >£1,000,000 buildings cover</span
                  >
                  as standard. The sum insured must cover the cost of rebuilding
                  your home.
                </div>
              </div>
              <div class="d-flex flex-column flex-lg-row mt-5 mt-xl-8">
                <div class="d-flex mr-2 flex-basis-85">
                  What amount of excess on your buildings cover is required?
                </div>
                <BaseMoneySpinner
                  :small="$vuetify.breakpoint.lgAndUp ? true : false"
                  :config="configExcess"
                  v-model="coverLocal.buildingsCover.excess"
                  class="mt-3 mt-xl-0 pa-0 flex-grow-1"
                  aria-label="Buildings cover excess amount"
                />
              </div>
            </template>
            <template v-if="isContentsCoverVisible">
              <div
                class="d-flex align-center"
                :class="[{ 'mt-10': isBuildingsCover }]"
              >
                <h4 class="font-24 font-lg-20">Contents cover</h4>
                <div class="ml-2">
                  <v-icon
                    v-if="contentsCoverDialogComponent"
                    color="#106175"
                    size="15"
                    class="text-cursor"
                    @click="
                      showModal({
                        dialogComponentName: contentsCoverDialogComponent,
                        customClass: 'pb-10 dialog-contents__wrapper',
                      })
                    "
                    >mdi-help-circle</v-icon
                  >
                  <BaseTooltip
                    v-else
                    :text="TOOL_TIP_TEXT.Contents"
                    icon="mdi-help-circle"
                  />
                </div>
              </div>
              <div class="d-flex flex-column flex-lg-row mt-2 align-xl-center">
                <div class="d-flex flex-basis-85 pr-lg-15">
                  {{ contentCoverLabel }}
                </div>
                <BaseMoneySpinner
                  :small="$vuetify.breakpoint.lgAndUp ? true : false"
                  v-model="coverLocal.contentsCover.amount"
                  :config="coverLevelConfig"
                  class="mt-3 mt-xl-0 pa-0 flex-grow-1"
                  aria-label="Contents cover amount"
                />
              </div>
              <p
                v-if="hintTextContentCoverRequired"
                class="hint-text mt-4 align-xl-center"
              >
                {{ hintTextContentCoverRequired }}
              </p>
              <div
                class="d-flex flex-column flex-lg-row mt-5 mt-xl-8 align-xl-center"
              >
                <div class="d-flex flex-basis-85 pr-lg-15">
                  <span>{{ lableContentCoverExcess }}</span>
                </div>
                <BaseMoneySpinner
                  :small="$vuetify.breakpoint.lgAndUp ? true : false"
                  :config="configExcess"
                  v-model="coverLocal.contentsCover.excess"
                  class="mt-3 mt-xl-0 pa-0 flex-grow-1"
                  aria-label="Contents cover excess amount"
                />
              </div>
              <p
                v-if="hintTextContentCoverExcess"
                class="hint-text mt-4 align-xl-center"
              >
                {{ hintTextContentCoverExcess }}
              </p>
            </template>
            <h4 class="font-20 mt-10">Additional cover options</h4>
            <!-- Child emits true/false directly, not a toggle signal -->
            <DrawerCustomiseCoverContentAdditionalCoverOptions
              :addOns="ADD_ONS"
              :productGroup="productGroup"
              @setDrawerRentProtectionLegalExpensesEligibilityCheck="
                isOpenDrawerRentProtectionLegalExpensesEligibilityCheck = $event
              "
            />

            <template
              v-if="isContentsCoverVisible && productGroup !== LANDLORDS"
            >
              <template v-if="productGroup === TENANTS_CONTENTS">
                <!-- worldwide belongings -->
                <div class="border-r10 pa-4 pt-6 my-5">
                  <div class="d-xl-flex align-xl-start">
                    <div class="d-flex">
                      <v-img
                        :src="
                          require('@/assets/images/worldwide-belongings.png')
                        "
                        transition="scale-transition"
                        height="104"
                        width="72"
                        contain
                        alt="quote.name"
                      />
                      <div class="ml-3">
                        <h4 class="font-18">
                          Would you like worldwide belongings cover?
                        </h4>
                        <p class="mt-3 mb-4 font-16">
                          This provides cover for accidental damage, or loss, to
                          your personal possessions like bikes, jewellery and
                          mobiles away from your home; anywhere in the world.
                        </p>
                        <div>
                          <a
                            class="align-self-start accent--text font-weight-semi-bold"
                            href="javascript:"
                            aria-label="What is included in this?"
                            @click.prevent="
                              showWhatsIncluded(worldwideBelongingsModalData)
                            "
                            >What is included in this?</a
                          >
                        </div>
                      </div>
                    </div>

                    <BaseButton
                      :text="isAddedWorldwideBelongsCover ? 'REMOVE' : 'ADD'"
                      height="60"
                      min-width="100"
                      rounded
                      depressed
                      :type="isAddedWorldwideBelongsCover ? 'text' : ''"
                      :block="$vuetify.breakpoint.xl ? false : true"
                      :color="isAddedWorldwideBelongsCover ? '' : 'psorange'"
                      :icon="
                        isAddedWorldwideBelongsCover ? 'mdi-minus-circle' : ''
                      "
                      :class="
                        !isAddedWorldwideBelongsCover
                          ? 'font-weight-bold'
                          : 'border-1 ml-lg-3'
                      "
                      class="font-20 py-6 mt-5 mt-xl-0"
                      iconColor="var(--red-500)"
                      @click="
                        isAddedWorldwideBelongsCover =
                          !isAddedWorldwideBelongsCover
                      "
                      aria-label="Worldwide belongings cover"
                    />
                  </div>
                  <div v-if="isAddedWorldwideBelongsCover">
                    <DrawerCustomiseCoverSpecifiedItems
                      ref="personalPossessions"
                      :productGroup="productGroup"
                      :cover="coverLocal"
                      :specifiedItemsType="PERSONAL_POSSESSIONS"
                      @showWhatsIncluded="showWhatsIncluded"
                    />
                    <div class="mt-10">
                      <h3 class="font-20">
                        Choose a value to cover all other items you’d like to be
                        insured when taken away from the home:
                      </h3>
                      <div
                        style="background-color: #354048"
                        class="white--text d-flex justify-space-between font-16 mt-4 font-weight-bold"
                      >
                        <div class="col-12">Amount</div>
                      </div>
                      <div
                        class="d-flex flex-column pa-3"
                        style="background-color: #f3f3f3"
                      >
                        <BaseSelect
                          name="itemType"
                          hideDetails
                          :rules="{ required: true }"
                          :items="
                            UNSPECIFIED_PERSONAL_POSSESSIONS.unspecifiedAmounts
                          "
                          :isEnableSortOrderListItems="false"
                          :label="
                            !$vuetify.breakpoint.lgAndUp ? 'Item type' : ''
                          "
                          v-model="unspecifiedPpAmount"
                          :dense="$vuetify.breakpoint.lgAndUp"
                          aria-label="Unspecified personal possessions amount"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="productGroup !== TENANTS_CONTENTS">
                <!-- personal possessions -->
                <div class="border-r10 pa-4 pt-6 my-5">
                  <div class="d-xl-flex align-xl-start">
                    <div class="d-flex">
                      <v-img
                        :src="
                          require('@/assets/images/personal-possessions.png')
                        "
                        transition="scale-transition"
                        height="48"
                        width="72"
                        contain
                        alt="quote.name"
                      />
                      <div class="ml-3">
                        <h4 class="font-18">Personal possessions cover</h4>
                        <p class="mt-3 mb-4 font-16">
                          Cover for accidental loss or damage to items usually
                          worn or carried, like jewellery or mobile phones both
                          in the UK and abroad.
                        </p>
                        <div>
                          <a
                            class="align-self-start accent--text font-weight-semi-bold"
                            href="javascript:"
                            aria-label="What is included in this?"
                            @click.prevent="
                              showWhatsIncluded(personalPossessionsModalData)
                            "
                            >What is included in this?</a
                          >
                        </div>
                      </div>
                    </div>

                    <BaseButton
                      :text="isAddedPersonalPossessionsCover ? 'REMOVE' : 'ADD'"
                      height="60"
                      min-width="100"
                      rounded
                      depressed
                      :type="isAddedPersonalPossessionsCover ? 'text' : ''"
                      :block="$vuetify.breakpoint.xl ? false : true"
                      :color="isAddedPersonalPossessionsCover ? '' : 'psorange'"
                      :icon="
                        isAddedPersonalPossessionsCover
                          ? 'mdi-minus-circle'
                          : ''
                      "
                      :class="
                        !isAddedPersonalPossessionsCover
                          ? 'font-weight-bold'
                          : 'border-1 ml-lg-3'
                      "
                      class="font-20 py-6 mt-5 mt-xl-0"
                      iconColor="var(--red-500)"
                      @click="
                        isAddedPersonalPossessionsCover =
                          !isAddedPersonalPossessionsCover
                      "
                      aria-label="Worldwide belongings cover"
                    />
                  </div>
                  <div v-if="isAddedPersonalPossessionsCover">
                    <DrawerCustomiseCoverSpecifiedItems
                      ref="personalPossessions"
                      :productGroup="productGroup"
                      :cover="coverLocal"
                      :specifiedItemsType="PERSONAL_POSSESSIONS"
                      @showWhatsIncluded="showWhatsIncluded"
                    />
                    <div class="mt-10">
                      <h3 class="font-20">
                        Choose a value to cover all other items you’d like to be
                        insured when taken away from the home:
                      </h3>
                      <div
                        style="background-color: #354048"
                        class="white--text d-flex justify-space-between font-16 mt-4 font-weight-bold"
                      >
                        <div class="col-12">Amount</div>
                      </div>
                      <div
                        class="d-flex flex-column pa-3"
                        style="background-color: #f3f3f3"
                      >
                        <BaseSelect
                          name="itemType"
                          hideDetails
                          :rules="{ required: true }"
                          :items="
                            UNSPECIFIED_PERSONAL_POSSESSIONS.unspecifiedAmounts
                          "
                          :isEnableSortOrderListItems="false"
                          :label="
                            !$vuetify.breakpoint.lgAndUp ? 'Item type' : ''
                          "
                          v-model="unspecifiedPpAmount"
                          :dense="$vuetify.breakpoint.lgAndUp"
                          aria-label="Unspecified personal possessions amount"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <DrawerCustomiseCoverSpecifiedItems
                ref="valuables"
                :productGroup="productGroup"
                :cover="coverLocal"
                :specifiedItemsType="VALUABLES"
                @showWhatsIncluded="showWhatsIncluded"
              />
            </template>
          </div>
        </div>

        <div
          class="drawer-footer d-flex flex-column pa-5"
          :class="
            isOpenDrawerRentProtectionLegalExpensesEligibilityCheck
              ? 'hide-content'
              : ''
          "
        >
          <template v-if="productGroup === TENANTS_CONTENTS">
            <BaseAlert
              v-if="isPPAndWWBIncomplete"
              color="var(--orange-100)"
              class="pa-4 border-sm border-solid border-warning"
            >
              <div class="d-flex align-center">
                <div class="mr-3">
                  <v-icon color="var(--orange-500)">mdi-alert</v-icon>
                </div>
                <div class="primary--text">
                  <span class="font-weight-bold">Note:&nbsp;</span>A value must
                  be provided for worldwide belongings cover. Please enter an
                  amount or remove the cover type.
                </div>
              </div>
            </BaseAlert>

            <BaseAlert
              v-if="isWWBItemsWorthOverLimit"
              color="var(--red-100)"
              class="pa-4 border-sm border-solid border-error"
            >
              <div class="d-flex">
                <div class="mr-3">
                  <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
                </div>
                <div class="primary--text">
                  <span class="font-weight-bold">Sorry!&nbsp;</span>The total
                  value of worldwide belongings must not exceed £15,000. Please
                  reduce the amount.
                </div>
              </div>
            </BaseAlert>

            <BaseAlert
              v-if="isValuablesOverContentsCoverLimit"
              color="var(--orange-100)"
              class="pa-4 border-sm border-solid border-warning"
            >
              <div class="d-flex">
                <div class="mr-3">
                  <v-icon color="var(--orange-500)">mdi-alert</v-icon>
                </div>
                <div class="primary--text">
                  <span class="font-weight-bold">Sorry!&nbsp;</span>The value of
                  the valuables you have specified is greater than the level of
                  contents cover you have selected. Please increase your chosen
                  level of contents cover to continue.
                </div>
              </div>
            </BaseAlert>
          </template>

          <template
            v-if="![LANDLORDS, TENANTS_CONTENTS].includes(productGroup)"
          >
            <div v-if="isValuablesOverLimit" class="mb-5">
              <BaseAlert
                color="var(--red-100)"
                class="ma-0 pa-4 border-sm border-solid border-error"
              >
                <div class="d-flex">
                  <div class="mr-3">
                    <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
                  </div>
                  <div class="primary--text">
                    <span class="font-weight-bold">Sorry!&nbsp;</span>The total
                    value of valuables must not exceed 30% of contents cover.
                    Please increase your Contents cover or reduce your valuables
                    amount.
                  </div>
                </div>
              </BaseAlert>
            </div>

            <div v-if="isPPItemsWorthOverLimit" class="mb-5">
              <BaseAlert
                color="var(--red-100)"
                class="ma-0 pa-4 border-sm border-solid border-error"
              >
                <div class="d-flex">
                  <div class="mr-3">
                    <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
                  </div>
                  <div class="primary--text">
                    <span class="font-weight-bold">Sorry!&nbsp;</span>The total
                    value of valuables must not exceed 30% of contents cover.
                    Please increase your Contents cover or reduce your valuables
                    amount.
                  </div>
                </div>
              </BaseAlert>
            </div>

            <BaseAlert
              v-if="isPPAndWWBIncomplete"
              color="var(--orange-100)"
              class="pa-4 border-sm border-solid border-warning"
            >
              <div class="d-flex align-center">
                <div class="mr-3">
                  <v-icon color="var(--orange-500)">mdi-alert</v-icon>
                </div>
                <div class="primary--text">
                  <span class="font-weight-bold">Note:&nbsp;</span>A value must
                  be provided for personal possessions cover. Please enter an
                  amount or remove the cover type.
                </div>
              </div>
            </BaseAlert>
          </template>

          <BaseAlert
            v-if="isValuablesEmpty"
            color="var(--orange-100)"
            class="border-sm border-solid border-warning pa-4"
          >
            <div class="d-flex align-center">
              <div class="mr-3">
                <v-icon color="var(--orange-500)">mdi-alert</v-icon>
              </div>
              <div class="primary--text">
                <span class="font-weight-bold">Note:&nbsp;</span>A value must be
                provided for valuables. Please enter an amount.
              </div>
            </div>
          </BaseAlert>

          <BaseButton
            text="BACK TO QUOTES"
            height="68"
            block
            color="#106175"
            class="font-weight-bold font-20 rounded-pill white--text"
            icon="mdi-chevron-left"
            iconSize="30"
            :loading="isRefreshingCover"
            loadingIconColor="white"
            @click="backToQuotes"
            :disabled="isDisableBtn"
            v-if="!isDrawerStateChanged"
          />
          <template v-else>
            <BaseButton
              text="APPLY CHANGES"
              height="68"
              block
              color="#FAB519"
              text-color="black--text"
              icon-color="black"
              loadingIconColor="black"
              class="font-weight-bold font-20 rounded-pill white--text"
              type="submit"
              icon="mdi-refresh"
              iconSize="30"
              :loading="isRefreshingCover"
              @click="refreshCover"
              :disabled="isDisableBtn"
            />
            <BaseButton
              text="Cancel"
              flat
              link
              depressed
              color="black--text"
              ripple
              small
              class="font-weight-medium px-1 d-flex align-self-center font-18 mt-4"
              @click="cancelChanges()"
            />
          </template>
        </div>
      </div>
    </v-navigation-drawer>
    <BaseDialog
      ref="showWhatsIncludedPersonalPossessions"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-6 mb-n1"
      :headerDivider="true"
      dark2Icon
      persistent
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedPersonalPossessions
          @cancelWhatsIncluded="cancelWhatsIncluded"
        />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="showWhatsIncludedPersonalPossessionsTenantsContent"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-6 mb-n1"
      :headerDivider="true"
      dark2Icon
      persistent
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedPersonalPossessionsTenantsContent
          @cancelWhatsIncluded="cancelWhatsIncluded"
        />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="showWhatsIncludedValuablesInHome"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-6 mb-n1"
      headerDivider
      dark2Icon
      persistent
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedValuablesInHome
          @cancelWhatsIncluded="cancelWhatsIncluded"
        />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="showWhatsIncludedValuablesInTenantsContents"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-6 mb-n1"
      headerDivider
      dark2Icon
      persistent
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedValuablesInTenantsContents
          @cancelWhatsIncluded="cancelWhatsIncluded"
        />
      </template>
    </BaseDialog>

    <v-overlay :value="overlay" :z-index="100" :opacity="0" />
  </div>
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import { QUESTIONS_MODULE, DIALOG_MODULE } from "@/store/types/module-types";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseMoneySpinner from "@/components/BaseComponents/BaseMoneySpinner.vue";
import BaseTooltip from "@/components/BaseComponents/BaseTooltip.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";
import { AMOUNT_INSURED_FORMAT } from "@/constants/currency-format.constants";
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";
import { formAnswersToApiAnswers, numbersToWords } from "@/common/answers";
import { TOOL_TIP_TEXT } from "@/constants/common.constants";
import {
  PERSONAL_POSSESSIONS,
  UNSPECIFIED_PERSONAL_POSSESSIONS,
  WWB_ITEMS_LIMIT,
} from "@/constants/possessions-options";
import { VALUABLES } from "@/constants/valuable-options";
import {
  ADD_ONS,
  ADD_ONS_ANNUAL_COST,
} from "@/constants/additional-cover-options";
import { dereference } from "@/common/transforms";
import { createConfigContentCover } from "@/common/configHelpers";
import { mapGetters, mapState } from "vuex";
import DrawerCustomiseCoverSpecifiedItems from "@/components/QuoteResult/DrawerCustomiseCoverSpecifiedItems.vue";
import { REGEXP_PP_ITEM_VALUE } from "@/constants/regex-patterns.constants";
import DrawerCustomiseCoverContentAdditionalCoverOptions from "@/components/QuoteResult/landlords/DrawerCustomiseCoverContentAdditionalCoverOptions.vue";
import {
  HOME,
  LANDLORDS,
  TENANTS_CONTENTS,
} from "@/constants/questionset.constants";
import { CONTENTS_COVER } from "@/constants/label.constants";
import { scrollToElement } from "@/common/utils";

export default {
  components: {
    BaseButton,
    BaseMoneySpinner,
    BaseTooltip,
    BaseSelect,
    BaseAlert,
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentWhatsIncludedPersonalPossessions: () =>
      import(
        "@/components/QuoteResult/DialogContentWhatsIncludedPersonalPossessions.vue"
      ),
    DialogContentWhatsIncludedPersonalPossessionsTenantsContent: () =>
      import(
        "@/components/QuoteResult/DialogContentWhatsIncludedPersonalPossessionsTenantsContent.vue"
      ),
    DialogContentWhatsIncludedValuablesInHome: () =>
      import(
        "@/components/QuoteResult/DialogContentWhatsIncludedValuablesInHome"
      ),
    DialogContentWhatsIncludedValuablesInTenantsContents: () =>
      import(
        "@/components/QuoteResult/DialogContentWhatsIncludedValuablesInTenantsContents"
      ),
    DrawerCustomiseCoverSpecifiedItems,
    DrawerCustomiseCoverContentAdditionalCoverOptions,
  },
  mixins: [BuildingsContentsVisibility],
  props: {
    value: Boolean,
    cover: Object,
    valuables: Array,
    productGroup: String,
    contentsCoverDialogComponent: String,
    contentCoverLabel: {
      type: String,
      default: () => CONTENTS_COVER,
    },
    additionalCoverAnnualCost: {
      type: Object,
      default: () => ADD_ONS_ANNUAL_COST,
    },
    additionalCover: {
      type: Array,
      default: () => ADD_ONS,
    },
    personalPossessions: {
      type: Array,
      default: () => PERSONAL_POSSESSIONS,
    },
    unspecifiedPersonalPossessions: {
      type: Object,
      default: () => UNSPECIFIED_PERSONAL_POSSESSIONS,
    },
    hintTextContentCoverRequired: {
      type: String,
      default: null,
    },
    hintTextContentCoverExcess: {
      type: String,
      default: null,
    },
    lableContentCoverExcess: {
      type: String,
      default: "What amount of excess on your contents cover is required?",
    },
    configExcessExternal: {
      type: Object,
      default: () => createConfigContentCover(50, 500, 50),
    },
  },
  data() {
    return {
      AMOUNT_INSURED_FORMAT,
      VALUABLES: this.valuables ? this.valuables : VALUABLES,
      ADD_ONS: this.additionalCover,
      addOnsAnnualCost: this.additionalCoverAnnualCost,
      PERSONAL_POSSESSIONS: this.personalPossessions,
      UNSPECIFIED_PERSONAL_POSSESSIONS: this.unspecifiedPersonalPossessions,
      TOOL_TIP_TEXT,
      REGEXP_PP_ITEM_VALUE,
      unspecifiedPpAmount: null,
      coverLocal: dereference(this.cover),
      coverLocalStringified: null,
      isRefreshingCover: false,
      valueLocal: this.value,
      configContents: createConfigContentCover(25000, 100000, 75000),
      configContentsLandlord: createConfigContentCover(10000, 50000, 10000),
      configContentsTenants: createConfigContentCover(10000, 50000, 10000),
      configContentsRentedOnly: createConfigContentCover(10000, 50000, 20000),
      configExcess: this.configExcessExternal,
      navDrawerContent: null,
      isAddedPersonalPossessionsCover: false,
      isAddedWorldwideBelongsCover: false,
      LANDLORDS,
      TENANTS_CONTENTS,
      landlordContentsLevel: null,
      excessBuildings: null,
      excessContents: null,
      isOpenDrawerRentProtectionLegalExpensesEligibilityCheck: false,
      personalPossessionsModalData: {
        ref: "showWhatsIncludedPersonalPossessions",
        helpDialogTitle: "What is included in this?",
      },
      worldwideBelongingsModalData: {
        ref: "showWhatsIncludedPersonalPossessionsTenantsContent",
        helpDialogTitle: "What is included in this?",
      },
      overlay: false,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getAnswer",
      "isDrawerCustomiseCoverPopulatedFromApi",
      "isProductTransferInProgress",
      "isQuotePriced",
      "coverSelection",
    ]),
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
    coverLevelConfig() {
      const ownershipProperty = this.getAnswer("OwnershipProperty");

      if (
        ownershipProperty === "RentedFromLocalAuthorityOrHousingAssociation" ||
        ownershipProperty === "RentedFromPrivateLandlord"
      ) {
        return this.configContentsRentedOnly;
      }

      switch (this.productGroup) {
        case LANDLORDS:
          return this.configContentsLandlord;
        case TENANTS_CONTENTS:
          return this.configContentsTenants;
        default:
          return this.configContents;
      }
    },
    isValuablesOverLimit() {
      let valuablesTotalWorth = this.calValuables;
      let isValuablesOverLimit;

      isValuablesOverLimit =
        valuablesTotalWorth > (this.coverLocal.contentsCover.amount / 100) * 30; // 30 percent of whatever contents cover is selected

      return isValuablesOverLimit;
    },
    isPPItemsWorthOverLimit() {
      let ppAddedItemsTotalWorth = this.calPpAddedItemsTotalWorth;
      let isPPItemsOverLimit;

      isPPItemsOverLimit =
        ppAddedItemsTotalWorth >
        (this.coverLocal.contentsCover.amount / 100) * 30; // 30 percent of whatever contents cover is selected

      return isPPItemsOverLimit;
    },
    isValuablesEmpty() {
      if (!this.VALUABLES[0].form.isShow) return false;
      return this.VALUABLES[0].items.length === 0;
    },
    isPPItemsEmpty() {
      const isShowPP = this.isAddedPersonalPossessionsCover;
      const isShowWWB = this.isAddedWorldwideBelongsCover;

      if (!isShowPP && !isShowWWB) return false;
      return this.PERSONAL_POSSESSIONS[0].items.length === 0;
    },
    isUnspecifiedPpAmountEmpty() {
      if (!this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow) return false;
      return (
        this.unspecifiedPpAmount === null || this.unspecifiedPpAmount === "Zero"
      );
    },
    isPPAndWWBIncomplete() {
      return this.isPPItemsEmpty && this.isUnspecifiedPpAmountEmpty;
    },
    isWWBItemsWorthOverLimit() {
      const ppAddedItemsTotalWorth = this.calPpAddedItemsTotalWorth;
      return ppAddedItemsTotalWorth > WWB_ITEMS_LIMIT;
    },
    isValuablesOverContentsCoverLimit() {
      const valuablesTotalWorth = this.calValuables;
      return valuablesTotalWorth > this.coverLocal.contentsCover.amount;
    },
    isDisableBtn() {
      if (this.productGroup === TENANTS_CONTENTS) {
        return (
          this.isValuablesEmpty ||
          this.isPPAndWWBIncomplete ||
          this.isWWBItemsWorthOverLimit ||
          this.isValuablesOverContentsCoverLimit
        );
      }
      return (
        this.isValuablesEmpty ||
        this.isPPAndWWBIncomplete ||
        this.isPPItemsWorthOverLimit ||
        this.isValuablesOverLimit
      );
    },
    calValuables() {
      let valuablesTotalWorth = 0;

      this.VALUABLES.forEach((valuable) => {
        if (!valuable.form.isShow) return;
        valuable.items.forEach((item) => {
          valuablesTotalWorth += parseFloat(item.value);
        });
      });

      return valuablesTotalWorth;
    },
    calPpAddedItemsTotalWorth() {
      let ppAddedItemsTotalWorth = 0;
      this.PERSONAL_POSSESSIONS.forEach((pp) => {
        if (!pp.form.isShow) return;
        pp.items.forEach((item) => {
          ppAddedItemsTotalWorth += parseFloat(item.value);
        });
      });

      if (
        this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow &&
        this.unspecifiedPpAmount
      ) {
        ppAddedItemsTotalWorth += parseFloat(
          this.UNSPECIFIED_PERSONAL_POSSESSIONS.unspecifiedAmounts.find(
            (itemType) => itemType.value === this.unspecifiedPpAmount
          )?.numberValue
        );
      }
      return ppAddedItemsTotalWorth;
    },
    showLink() {
      return (data) => data && data.showLink;
    },
    coverDataStringified() {
      return JSON.stringify({
        coverLocal: this.coverLocal,
        ADD_ONS: this.ADD_ONS,
        VALUABLES: this.VALUABLES,
        PERSONAL_POSSESSIONS: this.PERSONAL_POSSESSIONS,
        UNSPECIFIED_PERSONAL_POSSESSIONS: this.UNSPECIFIED_PERSONAL_POSSESSIONS,
        unspecifiedPpAmount: this.unspecifiedPpAmount,
        isAddedPersonalPossessionsCover: this.isAddedPersonalPossessionsCover,
        isAddedWorldwideBelongsCover: this.isAddedWorldwideBelongsCover,
      });
    },
    isContentsCoverVisible() {
      return this.isContentsOnlyProduct || this.isContentsCover;
    },
    isDrawerStateChanged() {
      return this.coverDataStringified !== this.coverLocalStringified;
    },
    // return lower specified selection that caps out at 15,000
    // for contentsCover amount lower than 75,000 for Home journey
    unspecifiedPersonalPossessionsItems() {
      if (
        this.productGroup !== HOME ||
        !this.UNSPECIFIED_PERSONAL_POSSESSIONS.lowerUnspecifiedAmounts
      ) {
        return this.UNSPECIFIED_PERSONAL_POSSESSIONS.unspecifiedAmounts;
      }

      return this.coverLocal.contentsCover.amount < 75000
        ? this.UNSPECIFIED_PERSONAL_POSSESSIONS.lowerUnspecifiedAmounts
        : this.UNSPECIFIED_PERSONAL_POSSESSIONS.unspecifiedAmounts;
    },
  },
  watch: {
    value(val) {
      this.valueLocal = val;
    },
    valueLocal(val) {
      this.$emit("input", val);

      // when the drawer opens/closes we set the scroll position to the top
      // the second arg is the target element
      // without this Vuetify will default to the page rather than the element
      this.$vuetify.goTo(0, { container: this.navDrawerContent });
    },
    cover(val) {
      this.coverLocal = dereference(val);
      this.coverLocalStringified = this.coverDataStringified;
    },
    additionalCover(newVal) {
      this.ADD_ONS = newVal;
    },
    "coverSelection.contentsAd": function (newValue) {
      this.ADD_ONS.forEach((option) => {
        if (option.code === "ad") {
          option.isAdded = newValue;
        }
      });
    },
    isAddedPersonalPossessionsCover(val) {
      this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow = val;

      if (!val) {
        this.unspecifiedPpAmount = null;
        this.PERSONAL_POSSESSIONS[0].items = [];
        this.PERSONAL_POSSESSIONS[0].form.isShow = false;
      }
    },
    isAddedWorldwideBelongsCover(val) {
      this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow = val;

      if (!val) {
        this.unspecifiedPpAmount = null;
        this.PERSONAL_POSSESSIONS[0].items = [];
        this.PERSONAL_POSSESSIONS[0].form.isShow = false;
      }
    },
    VALUABLES: {
      handler(val) {
        if (!val[0].form.isShow) return;

        this.$nextTick(() => {
          // setScroll is defined in DrawerCustomiseCoverSpecifiedItems
          this.$refs.valuables.setScroll();
        });
      },
      deep: true,
    },
    PERSONAL_POSSESSIONS: {
      handler(val) {
        if (!val[0].form.isShow) return;

        this.$nextTick(() => {
          // setScroll is defined in DrawerCustomiseCoverSpecifiedItems
          this.$refs.personalPossessions.setScroll();
        });
      },
      deep: true,
    },
    UNSPECIFIED_PERSONAL_POSSESSIONS: {
      handler(val) {
        if (!val.isShow) return;

        this.$nextTick(() => {
          scrollToElement(this.$refs.unspecifiedPersonalPossessions);
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.ADD_ONS = dereference(this.ADD_ONS); //This will stop storing the Add on values so that they could be populated from question set
    this.ADD_ONS.forEach((addon) => {
      if (addon.code === "he") addon.annualCost = this.addOnsAnnualCost.he;
      if (addon.code === "le") addon.annualCost = this.addOnsAnnualCost.le;
      if (addon.code === "ad") addon.annualCost = this.addOnsAnnualCost.ad;
      if (this.productGroup === LANDLORDS && addon.code === "rp")
        addon.annualCost = this.addOnsAnnualCost.rp;
    });

    // set which element we want to control scrolling
    this.navDrawerContent = this.$refs.customiseDrawer.$el.querySelector(
      "div.v-navigation-drawer__content"
    );

    if (
      !this.isDrawerCustomiseCoverPopulatedFromApi &&
      this.productGroup !== LANDLORDS
    ) {
      this.loadPersonalPossessions();
      this.loadValuables();
      this.loadAddOns();
      this.setIsDrawerCustomiseCoverPopulatedFromApi(true);
    }
    if (
      this.cover.unspecifiedPpAmount === "Zero" ||
      !this.cover.unspecifiedPpAmount
    ) {
      this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow = false;
    } else {
      this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow = true;
      this.unspecifiedPpAmount = this.cover.unspecifiedPpAmount ?? null;
      this.isAddedPersonalPossessionsCover = true;
      if (this.productGroup === TENANTS_CONTENTS) {
        this.isAddedWorldwideBelongsCover = true;
      }
    }

    if (this.productGroup === TENANTS_CONTENTS) {
      if (this.cover.worldwideBelongings.length) {
        this.isAddedWorldwideBelongsCover = true;
      }
    }

    this.coverLocalStringified = this.coverDataStringified;

    this.landlordContentsLevel = this.questionMap.LandlordContentsLevel;
    this.excessBuildings = this.questionMap.ExcessBuildings;
    this.excessContents = this.questionMap.ExcessContents;
  },
  updated() {
    // Any time something updates this component check whether a Product Transfer is taking place,
    // and if there is then clear down HE/LE, specified item and PP selections.
    if (this.isProductTransferInProgress) {
      this.clearAddOnsValuablesAndPPs();
      this.setProductTransferInProgressFlag(false);
    }
  },
  destroyed() {
    this.setIsDrawerCustomiseCoverPopulatedFromApi(false);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setCoverAnswers",
      "setCoverSelection",
      "getQuotes",
      "setIsDrawerCustomiseCoverPopulatedFromApi",
      "setProductTransferInProgressFlag",
    ]),
    ...mapMutations(DIALOG_MODULE, ["showModal"]),

    backToQuotes() {
      if (this.productGroup === TENANTS_CONTENTS) {
        const ad = this.ADD_ONS.find((addon) => addon.code == "ad");

        this.setCoverSelection({
          contentsAd: ad.isAdded,
        });
      }

      this.valueLocal = false;
    },
    cancelChanges() {
      if (this.isRefreshingCover) return;
      this.valueLocal = !this.valueLocal;
      this.coverLocal = dereference(this.cover);
      const coverDataParsed = JSON.parse(this.coverLocalStringified);

      this.ADD_ONS = coverDataParsed.ADD_ONS;
      this.ADD_ONS.forEach((addon) => {
        if (addon.code == "he") addon.annualCost = this.addOnsAnnualCost.he;
        if (addon.code == "le") addon.annualCost = this.addOnsAnnualCost.le;
        if (addon.code == "ad") {
          addon.annualCost = this.addOnsAnnualCost.ad;
          this.setCoverSelection({
            contentsAd: addon.isAdded,
          });
        }
        if (this.productGroup === LANDLORDS && addon.code === "rp")
          addon.annualCost = this.addOnsAnnualCost.rp;
      });

      this.VALUABLES = coverDataParsed.VALUABLES;

      this.PERSONAL_POSSESSIONS = coverDataParsed.PERSONAL_POSSESSIONS;

      this.UNSPECIFIED_PERSONAL_POSSESSIONS =
        coverDataParsed.UNSPECIFIED_PERSONAL_POSSESSIONS;

      this.unspecifiedPpAmount = coverDataParsed.unspecifiedPpAmount;

      this.isAddedPersonalPossessionsCover =
        coverDataParsed.isAddedPersonalPossessionsCover;

      if (this.productGroup === TENANTS_CONTENTS) {
        this.isAddedWorldwideBelongsCover =
          coverDataParsed.isAddedWorldwideBelongsCover;
      }
    },
    onSubmit(index, possession, passes, resetForm) {
      passes(() => {
        this.PERSONAL_POSSESSIONS[index].items.push({
          description: possession.form.descriptionFieldValue,
          value: this.removeComma(possession.form.valueFieldValue),
          itemType: possession.form.selectedItemType,
          text: possession.form.itemTypes.find(
            (itemType) => itemType.value === possession.form.selectedItemType
          )?.text,
        });

        this.PERSONAL_POSSESSIONS[index].form.descriptionFieldValue = null;
        this.PERSONAL_POSSESSIONS[index].form.valueFieldValue = null;

        if (
          this.PERSONAL_POSSESSIONS[index].form.selectedItemType !==
            "MobilePhones" &&
          this.PERSONAL_POSSESSIONS[index].form.selectedItemType !==
            "PedalCycles"
        ) {
          this.PERSONAL_POSSESSIONS[index].form.selectedItemType = null;
        }

        this.$nextTick(() => {
          resetForm();
        });

        this.$refs.nativeForm[index]
          ?.querySelectorAll(".money-format input")
          .forEach((input) => (input.value = ""));
      });
    },
    refreshCover() {
      if (this.isRefreshingCover) return;
      this.isRefreshingCover = true;

      const form = {
        buildingsCover: {
          ...this.coverLocal.buildingsCover,
        },
        contentsCover: {
          ...this.coverLocal.contentsCover,
        },
        additionalCoverOptions: [],
        personalPossessions: [],
        unspecifiedPpAmount: null,
      };

      this.ADD_ONS.forEach((option) => {
        form.additionalCoverOptions.push({
          isAdded: option.isAdded,
          code: option.code,
        });
      });

      for (const key in this.VALUABLES) {
        if (
          this.VALUABLES[key].form.isShow &&
          this.VALUABLES[key].items.length
        ) {
          form.personalPossessions.push({
            interfaceKeyPrefix: this.VALUABLES[key].interfaceKeyPrefix,
            items: this.VALUABLES[key].items,
          });
        }
      }

      for (const key in this.PERSONAL_POSSESSIONS) {
        if (
          this.PERSONAL_POSSESSIONS[key].form.isShow &&
          this.PERSONAL_POSSESSIONS[key].items.length
        ) {
          form.personalPossessions.push({
            interfaceKeyPrefix:
              this.PERSONAL_POSSESSIONS[key].interfaceKeyPrefix,
            items: this.PERSONAL_POSSESSIONS[key].items,
          });
        }
      }

      form.unspecifiedPpAmount = this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow
        ? this.unspecifiedPpAmount
        : "";

      if (this.productGroup === LANDLORDS) {
        this.questionMap.LandlordsEligibility.Value = true;

        const hasRpOrLe = form.additionalCoverOptions.some(
          (opt) => (opt.code === "rp" || opt.code === "le") && opt.isAdded
        );
        if (!hasRpOrLe) {
          this.questionMap.NewLetOrProductTransfer.Value = "";
        }

        if (this.coverLocal.contentsCover.amount)
          this.landlordContentsLevel.Value = numbersToWords(
            this.coverLocal.contentsCover.amount
          );

        if (this.coverLocal.contentsCover.excess)
          this.excessContents.Value = numbersToWords(
            this.coverLocal.contentsCover.excess
          );

        if (this.coverLocal.buildingsCover.excess)
          this.excessBuildings.Value = numbersToWords(
            this.coverLocal.buildingsCover.excess
          );
      }

      const answers = formAnswersToApiAnswers(form, this.productGroup);
      this.setCoverAnswers(answers);

      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      const accidentalDamage = form.additionalCoverOptions.find(
        (item) => item.code === "ad"
      );

      this.overlay = true;
      this.getQuotes(quoteRequestFields)
        .then(() => {
          this.isRefreshingCover = false;
          this.valueLocal = false;
          this.$emit("refreshCover", accidentalDamage?.isAdded);

          this.loadAddOns();
        })
        .catch((error) => {
          console.log("refreshCover error", error);
          this.isRefreshingCover = false;
          this.valueLocal = false;
        })
        .finally(() => {
          this.overlay = false;
        });
    },
    removeComma(value) {
      return value.split(",").join("");
    },
    loadAddOns() {
      if (
        this.isDrawerCustomiseCoverPopulatedFromApi ||
        this.productGroup === LANDLORDS
      )
        return;
      this.ADD_ONS.forEach((ao) => {
        if (ao.code === "he") {
          ao.isAdded = this.cover.homeEmergency;
          ao.isPriceAdded = this.cover.homeEmergency && this.isQuotePriced;
        }
        if (ao.code === "le") {
          ao.isAdded = this.cover.legalExpenses;
          ao.isPriceAdded = this.cover.legalExpenses && this.isQuotePriced;
        }
        if (ao.code === "ad") {
          ao.isAdded = this.cover.accidentalDamageContents;
          ao.isPriceAdded =
            this.cover.accidentalDamageContents && this.isQuotePriced;
        }
      });
    },
    loadPersonalPossessions() {
      this.PERSONAL_POSSESSIONS.forEach((pp) => {
        this.loadPpItems(pp);
      });
    },
    loadValuables() {
      this.VALUABLES.forEach((valuable) => {
        this.loadValuableItems(valuable);
      });
    },
    loadPpItems(personalPossessions) {
      personalPossessions.items = [];
      this.cover.ppItems.forEach((ppItemGroup) => {
        const apiPPItem = ppItemGroup.find(
          (item) => item.InterfaceKey === "PPItemType"
        );
        if (!apiPPItem) return;

        let itemText = "";
        const apiPPType = ppItemGroup.find((item) => {
          if (item.Value === personalPossessions.form.selectedItemType) {
            return item;
          }

          const localPPItemType = personalPossessions.form.itemTypes.find(
            (ppType) => ppType.value === item.Value
          );
          if (localPPItemType && item.Value === localPPItemType.value) {
            itemText = localPPItemType.text;
            return item;
          }
        });

        if (!apiPPType) return;
        personalPossessions.form.isShow = true;

        let item = {
          text: itemText,
          description: ppItemGroup.find(
            (item) => item.InterfaceKey === "PPItemDescription"
          )?.Value,
          value: ppItemGroup.find((item) => item.InterfaceKey === "PPItemValue")
            ?.Value,
          itemType: apiPPType?.Value,
        };
        personalPossessions.items.push(item);
      });
    },
    loadValuableItems(valuable) {
      valuable.items = [];
      this.cover.ppItems.forEach((valuableItemGroup) => {
        const apiValuableItemType = valuableItemGroup.find(
          (item) => item.InterfaceKey === "SpecifiedItemType"
        );
        if (!apiValuableItemType) return;

        const localValuableItemType = valuable.form.itemTypes.find(
          (valuableType) => valuableType.value === apiValuableItemType.Value
        );

        if (!localValuableItemType) return;
        valuable.form.isShow = true;

        let item = {
          text: localValuableItemType.text,
          description: valuableItemGroup.find(
            (item) => item.InterfaceKey === "SpecifiedItemDescription"
          )?.Value,
          value: valuableItemGroup.find(
            (item) => item.InterfaceKey === "SpecifiedItemValue"
          )?.Value,
          itemType: apiValuableItemType?.Value,
        };
        valuable.items.push(item);
      });
    },
    cancelWhatsIncluded(ref) {
      this.$refs[ref].cancel();
    },
    showWhatsIncluded(data) {
      this.$refs[data.ref].open(`${data.helpDialogTitle ?? data.title}`);
    },
    clearAddOnsValuablesAndPPs() {
      this.ADD_ONS.forEach((ao) => (ao.isAdded = false));
      this.VALUABLES[0].items = [];
      this.VALUABLES[0].form.isShow = false;
      this.PERSONAL_POSSESSIONS[0].items = [];
      this.PERSONAL_POSSESSIONS[0].form.isShow = false;
      this.UNSPECIFIED_PERSONAL_POSSESSIONS.isShow = false;
    },
    editCover() {
      this.valueLocal = false;
      this.$emit("editCover");
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/scss/AppComponents/_DrawerCustomizeCover.scss";

.hide-content {
  height: 0px !important;
  opacity: 0;
  padding: 0 !important;
}
</style>
